import { createContext, type FC, type PropsWithChildren, useContext } from "react";
import type { AuthFailureKind } from "../utils/errors";

/** What an app is told when a request the auth layer gates on failed. */
export interface AuthFailureReport {
  kind: AuthFailureKind;
  /** The GraphQL operation that failed, e.g. `MyAccount`. */
  operation: string;
}

export interface AuthContextValue {
  authClientId: string;
  /**
   * Reports an auth failure to the host app, which owns telemetry. Nothing throws on this path —
   * a swallowed failure renders a message — so PostHog's exception autocapture (`window.onerror`,
   * `window.onunhandledrejection`) structurally cannot see it. An explicit emit is the only signal.
   */
  onAuthFailure?: (failure: AuthFailureReport) => void;
}

const AuthContext = createContext<AuthContextValue>({
  authClientId: "",
});

export const AuthContextProvider: FC<PropsWithChildren<AuthContextValue>> = ({ children, ...rest }) => {
  return <AuthContext.Provider value={rest}>{children}</AuthContext.Provider>;
};

export function useAuthContext() {
  return useContext(AuthContext);
}
